'use client';

import { IconCross } from '@/icons/IconCross';
import IComponentProps from '@/interfaces/componentProps';
import { Transition } from '@headlessui/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const Popup = ({
  open,
  className = '',
  closePopup,
  children,
  contentClass,
  removeShadow = false,
}: {
  open: boolean;
  closePopup: () => void;
  contentClass?: string;
  removeShadow?: boolean;
} & IComponentProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        closePopup();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when popup is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, closePopup]);

  if (!mounted) {
    return null;
  }

  return createPortal(
    <Transition show={open}>
      <div className={`fixed inset-0 z-[300] flex h-full w-full items-center justify-center bg-transparent`} onClick={closePopup}>
        <Transition
          as={'div'}
          appear={true}
          className="absolute inset-0 flex h-full w-full bg-black bg-opacity-20"
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-all duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        />
        <Transition
          as={'div'}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          className={`relative z-[101] h-fit max-h-[calc(var(--popup-size)*1vh)] max-w-[calc(var(--popup-size)*1vw)] overflow-hidden ${className}`}
          enter="transition-all duration-300"
          enterFrom="max-xs:translate-y-full xs:scale-0"
          enterTo="max-xs:translate-y-0 xs:scale-100"
          leave="transition-all duration-300"
          leaveFrom="max-xs:translate-y-0 xs:scale-100"
          leaveTo="max-xs:translate-y-full xs:scale-0"
          style={{ boxShadow: removeShadow ? 'none' : '0px 0px 20px rgb(0 0 0 / 50%)' }}
        >
          <span
            className={`absolute right-0 top-0 z-[1000000] ml-auto flex aspect-square w-8 rounded-bl-[15px] bg-white bg-opacity-60 pb-0.5 pl-0.5 md:w-10`}
            style={{ boxShadow: '0 0 5px 3px rgb(0 0 0 / 25%)' }}
          >
            <IconCross onClick={closePopup} className={`m-auto block w-3 cursor-pointer md:w-4`} />
          </span>
          {children}
        </Transition>
      </div>
    </Transition>,
    document.getElementById('portal')!,
  );
};

export default Popup;
